/**
 * Componente: Auditoría — solicitudes de autorización de reimpresión.
 * Admin-only. Reutiliza los endpoints de solicitudes-impresion que ya
 * existen y están probados (Fase 3c paso 2b) — no se tocó nada de eso.
 * Dos pestañas: Pendientes (con acciones Aprobar/Rechazar) e Historial
 * (solo lectura, ya revisadas).
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import ConfirmarSolicitudModal from './ConfirmarSolicitudModal';
import {
  STROKE_DEGRADE,
  IconExito,
  IconRechazar,
  IconAdvertencia,
} from './icons';
import '../styles/padron.css';

const API_URL = process.env.REACT_APP_API_URL;

// Mismo criterio que Padron.jsx / Personal.jsx: íconos de botón de fila
// chicos, trazo en degradé de marca, sin insignia circular.
const ICONO_BOTON = { size: 14, stroke: STROKE_DEGRADE };

// Un solo mapa para las 3 clases de solicitud (reintento_impresion,
// reimpresion, reactivacion) — todas comparten la misma columna "Motivo"
// en esta pantalla, y motivo_categoria no se repite entre tipos.
const MOTIVO_LABELS = {
  sin_tarjetas: 'Sin tarjetas',
  sin_ribbon: 'Sin ribbon',
  fallo_conexion: 'Fallo de conexión con la impresora',
  cancelacion_erronea: 'Cancelación errónea',
  extravio_robo: 'Extravío o robo',
  error_impresion: 'Error de impresión',
  vencimiento_fuera_de_gracia: 'Vencimiento fuera de gracia',
  otro: 'Otro',
};

// fecha_solicitud/fecha_revision son TIMESTAMP completos ("YYYY-MM-DD
// HH:MM:SS") — formatearFecha (utils/fecha.js) es solo para columnas DATE
// puras (fecha_vencimiento y similares), pasarle esto la rompe.
const formatearFechaHora = (valor) => {
  if (!valor) return '—';
  const [fecha, hora] = valor.split(' ');
  const [anio, mes, dia] = fecha.split('-');
  return hora ? `${dia}/${mes}/${anio} ${hora.slice(0, 5)}` : `${dia}/${mes}/${anio}`;
};

const resolverMotivo = (s) => {
  const label = MOTIVO_LABELS[s.motivo_categoria] || s.motivo_categoria;
  return s.motivo_categoria === 'otro' && s.motivo_detalle ? `${label}: ${s.motivo_detalle}` : label;
};

const Auditoria = ({ onVolver, esAdmin }) => {
  const [tab, setTab] = useState('pendientes');
  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [confirmacion, setConfirmacion] = useState(null); // { tipo: 'aprobar'|'rechazar', solicitud }
  const [procesandoId, setProcesandoId] = useState(null);
  // "Revisado por" del historial es un id crudo (el endpoint no lo trae
  // resuelto a nombre, y no se tocó el backend para esto) — se resuelve
  // acá con GET /api/personal, que ya existe y ya trae nombre_completo.
  const [personalPorId, setPersonalPorId] = useState({});

  const cargarSolicitudes = useCallback(async (tabActual) => {
    try {
      setCargando(true);
      setError('');
      const token = sessionStorage.getItem('token');
      const params = tabActual === 'pendientes' ? { estado: 'pendiente' } : {};
      const { data } = await axios.get(`${API_URL}/solicitudes-impresion`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const lista = data.solicitudes || [];
      setSolicitudes(tabActual === 'historial' ? lista.filter((s) => s.estado !== 'pendiente') : lista);
    } catch (err) {
      console.error('Error al cargar solicitudes:', err);
      setError(err.response?.data?.mensaje || 'Error al cargar las solicitudes');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (!esAdmin) return;
    cargarSolicitudes(tab);
  }, [tab, esAdmin, cargarSolicitudes]);

  useEffect(() => {
    if (!esAdmin) return;
    const cargarPersonal = async () => {
      try {
        const token = sessionStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/personal`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const mapa = {};
        (data.personal || []).forEach((p) => {
          mapa[p.id] = p.nombre_completo;
        });
        setPersonalPorId(mapa);
      } catch (err) {
        // No bloquea la pantalla si falla — el historial cae al fallback
        // "Usuario #id" en vez de trabar toda la pestaña por esto.
        console.error('Error al cargar personal:', err);
      }
    };
    cargarPersonal();
  }, [esAdmin]);

  const pedirAprobar = (solicitud) => setConfirmacion({ tipo: 'aprobar', solicitud });
  const pedirRechazar = (solicitud) => setConfirmacion({ tipo: 'rechazar', solicitud });
  const cancelarConfirmacion = () => setConfirmacion(null);

  const confirmarAccion = async () => {
    if (!confirmacion) return;
    const { tipo, solicitud } = confirmacion;

    try {
      setProcesandoId(solicitud.id);
      setError('');
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${API_URL}/solicitudes-impresion/${solicitud.id}/${tipo}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfirmacion(null);
      setMensaje(tipo === 'aprobar' ? 'Solicitud autorizada' : 'Solicitud rechazada');
      await cargarSolicitudes(tab);
      setTimeout(() => setMensaje(''), 6000);
    } catch (err) {
      console.error('Error al procesar la solicitud:', err);
      setError(err.response?.data?.mensaje || 'Error al procesar la solicitud');
      setConfirmacion(null);
    } finally {
      setProcesandoId(null);
    }
  };

  if (!esAdmin) {
    return (
      <section className="padron-section">
        {onVolver && (
          <button type="button" className="btn-volver padron-btn-volver" onClick={onVolver}>
            <ArrowLeft size={16} /> Volver
          </button>
        )}
        <div className="padron-empty">
          <p>Esta sección es solo para administradores.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="padron-section">
      {onVolver && (
        <button type="button" className="btn-volver padron-btn-volver" onClick={onVolver}>
          <ArrowLeft size={16} /> Volver
        </button>
      )}

      <div className="padron-toolbar">
        <div>
          <h2 style={{ margin: 0 }}>Auditoría</h2>
          <p className="form-hint" style={{ marginTop: 4 }}>
            Solicitudes de autorización para reintentos de impresión.
          </p>
        </div>
      </div>

      <div className="auditoria-tabs">
        <button
          type="button"
          className={`auditoria-tab ${tab === 'pendientes' ? 'activo' : ''}`}
          onClick={() => setTab('pendientes')}
        >
          Pendientes
        </button>
        <button
          type="button"
          className={`auditoria-tab ${tab === 'historial' ? 'activo' : ''}`}
          onClick={() => setTab('historial')}
        >
          Historial
        </button>
      </div>

      {mensaje && (
        <div className="alert alert-success">
          <IconExito size={18} />
          <p>{mensaje}</p>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <IconAdvertencia size={18} />
          <p>{error}</p>
        </div>
      )}

      {cargando ? (
        <div className="padron-loading">
          <div className="loading-spinner"></div>
          <p>Cargando...</p>
        </div>
      ) : solicitudes.length === 0 ? (
        <div className="padron-empty">
          <p>{tab === 'pendientes' ? 'No hay solicitudes pendientes.' : 'Todavía no hay historial de solicitudes revisadas.'}</p>
        </div>
      ) : (
        <div className="padron-table-wrapper">
          <table className="padron-table">
            <thead>
              <tr>
                <th>Contribuyente</th>
                <th>Motivo</th>
                <th>Solicitado por</th>
                <th>Fecha de solicitud</th>
                {tab === 'historial' && <th>Estado</th>}
                {tab === 'historial' && <th>Revisado por</th>}
                {tab === 'historial' && <th>Fecha de revisión</th>}
                {tab === 'pendientes' && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {solicitudes.map((s) => (
                <tr key={s.id}>
                  <td data-label="Contribuyente">{s.apellido}, {s.contribuyente_nombre}</td>
                  <td data-label="Motivo">{resolverMotivo(s)}</td>
                  <td data-label="Solicitado por">{s.solicitado_por_nombre}</td>
                  <td data-label="Fecha de solicitud">{formatearFechaHora(s.fecha_solicitud)}</td>
                  {tab === 'historial' && (
                    <td data-label="Estado">
                      <span className={`badge-solicitud badge-solicitud-${s.estado}`}>
                        {s.estado === 'autorizado' ? 'Autorizado' : 'Rechazado'}
                      </span>
                    </td>
                  )}
                  {tab === 'historial' && (
                    <td data-label="Revisado por">
                      {s.revisado_por ? (personalPorId[s.revisado_por] || `Usuario #${s.revisado_por}`) : '—'}
                    </td>
                  )}
                  {tab === 'historial' && (
                    <td data-label="Fecha de revisión">{formatearFechaHora(s.fecha_revision)}</td>
                  )}
                  {tab === 'pendientes' && (
                    <td data-label="Acciones">
                      <div className="padron-acciones">
                        <button
                          className="btn-accion btn-editar"
                          onClick={() => pedirAprobar(s)}
                          disabled={procesandoId === s.id}
                        >
                          <IconExito {...ICONO_BOTON} /> Aprobar
                        </button>
                        <button
                          className="btn-accion btn-imprimir"
                          onClick={() => pedirRechazar(s)}
                          disabled={procesandoId === s.id}
                        >
                          <IconRechazar {...ICONO_BOTON} /> Rechazar
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmacion && (
        <ConfirmarSolicitudModal
          tipo={confirmacion.tipo}
          nombreContribuyente={`${confirmacion.solicitud.apellido}, ${confirmacion.solicitud.contribuyente_nombre}`}
          procesando={procesandoId === confirmacion.solicitud.id}
          onConfirmar={confirmarAccion}
          onCancelar={cancelarConfirmacion}
        />
      )}
    </section>
  );
};

export default Auditoria;
